import { Plane, Ship, Info, Zap, Coins } from "lucide-react";
import ModalModel from "./ModalModel";
import {
  INTERNATIONAL_SHIPPING_MODES,
  INTERNATIONAL_SHIPPING_WHY,
  INTERNATIONAL_SHIPPING_NOTES,
} from "../../constants/internationalShippingContent";
import { formatSeaFreightExample, formatShippingRate } from "../../utils/pricing";
import usePricing from "../../hooks/usePricing";

const MODE_MAP = {
  aerien: "avion",
  maritime: "bateau",
};

/**
 * Pop-up informative accueil — détail Avion Express / Fret Maritime.
 * @param {"aerien"|"maritime"|null} mode
 */
const TransportExplainModal = ({ mode, onClose }) => {
  const modeId = mode ? MODE_MAP[mode] : null;
  const data = modeId
    ? INTERNATIONAL_SHIPPING_MODES.find((m) => m.id === modeId)
    : null;

  // Appelé avant tout retour anticipé : l'ordre des hooks doit rester stable.
  const { pricing } = usePricing();

  if (!data) return null;

  const cost = formatShippingRate(data.apiValue, pricing);
  const exampleCost = data.example
    ? formatSeaFreightExample(data.example.volume, pricing)
    : null;

  const isAir = data.id === "avion";
  const Icon = isAir ? Plane : Ship;
  const TagIcon = isAir ? Zap : Coins;
  const accent = isAir
    ? { icon: "text-blue-500", bg: "bg-blue-50", border: "border-blue-100", tag: "text-blue-700" }
    : { icon: "text-teal-500", bg: "bg-teal-50", border: "border-teal-100", tag: "text-teal-700" };

  const whyMode = INTERNATIONAL_SHIPPING_WHY.modes.find((m) =>
    isAir
      ? m.title.toLowerCase().includes("avion")
      : m.title.toLowerCase().includes("bateau")
  );

  return (
    <ModalModel
      active
      onClose={onClose}
      title={data.title}
      icon={<Icon className={`w-5 h-5 ${accent.icon}`} />}
    >
      <div className="space-y-3 pb-2 max-h-[70vh] overflow-y-auto">
        <div className={`rounded-xl border ${accent.border} ${accent.bg} px-3.5 py-3.5 space-y-3`}>
          <div className={`flex items-center gap-1.5 ${accent.tag}`}>
            <TagIcon className="w-4 h-4 shrink-0" />
            <p className="text-sm font-bold">{data.tagline}</p>
          </div>

          <InfoRow label="Délai">{data.delay}</InfoRow>
          <InfoRow label="Tarification">
            {data.pricing}
            {data.pricingDetail ? (
              <span className="block text-gray-500 mt-1">{data.pricingDetail}</span>
            ) : null}
          </InfoRow>
          {cost && <InfoRow label="Coût">{cost}</InfoRow>}
          {data.idealFor && <InfoRow label="Idéal pour">{data.idealFor}</InfoRow>}
          <InfoRow label="Paiement">{data.payment}</InfoRow>

          {data.example && (
            <div className="rounded-lg bg-white/70 border border-teal-100/80 px-2.5 py-2 mt-1">
              <p className="text-xs font-semibold text-gray-600 mb-1">Exemple</p>
              <p className="text-xs text-gray-600 leading-relaxed">{data.example.intro}</p>
              {exampleCost && (
                <p className="text-xs text-gray-800 font-medium mt-1 leading-relaxed">
                  {exampleCost}
                </p>
              )}
            </div>
          )}

          {data.note && (
            <div className="flex gap-2 rounded-lg bg-amber-50 border border-amber-100 px-2.5 py-2 mt-1">
              <Info className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 leading-relaxed">
                <span className="font-semibold">Note : </span>
                {data.note}
              </p>
            </div>
          )}
        </div>

        {/* Pourquoi ces prix — filtré selon le mode cliqué */}
        <div className="rounded-xl border border-gray-100 bg-gray-50 px-3.5 py-3.5 space-y-2.5">
          <p className="text-sm font-bold text-gray-800">
            {INTERNATIONAL_SHIPPING_WHY.title}
          </p>
          <p className="text-xs text-gray-600 leading-relaxed">
            {INTERNATIONAL_SHIPPING_WHY.intro}
          </p>
          {whyMode && (
            <div className="rounded-lg bg-white border border-gray-100 px-2.5 py-2.5">
              <div className={`flex items-center gap-1.5 mb-1 ${accent.tag}`}>
                <Icon className="w-3.5 h-3.5 shrink-0" />
                <p className="text-xs font-bold">{whyMode.title}</p>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">{whyMode.text}</p>
            </div>
          )}
        </div>

        {/* Notes générales */}
        <div className="rounded-xl border border-amber-100 bg-amber-50/80 px-3.5 py-3 space-y-2">
          <p className="text-xs font-semibold text-amber-800">Note :</p>
          <ul className="space-y-1.5 list-disc list-outside pl-4">
            {INTERNATIONAL_SHIPPING_NOTES.map((note) => (
              <li key={note} className="text-xs text-amber-900/80 leading-relaxed">
                {note}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </ModalModel>
  );
};

const InfoRow = ({ label, children }) => (
  <div>
    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
      {label}
    </p>
    <p className="text-sm text-gray-700 mt-0.5 leading-relaxed">{children}</p>
  </div>
);

export default TransportExplainModal;
